import React from 'react';
import classNames from 'classnames';

import { IconCaret } from 'components/Icon/Caret';
import { VerticalContext } from 'lib/ContextTypes';
import {
  ECOMMERCE_DISCLAIMER,
  ECOMMERCE_ABOUT_URL_TODAY,
} from 'lib/commerceUtils';

import styles from './styles.module.scss';

/**
 * Generates the "Learn More" link based on the provided parameters.
 *
 * @param {string} vertical - The vertical type.
 * @param {boolean} isNoneDisclaimer - Flag indicating if it's a "none" disclaimer.
 * @param {boolean} hideLearnMore - Flag indicating if the "Learn More" link should be hidden.
 * @param {string} [learnMoreLinkOverride] - Optional override for the "Learn More" link URL.
 * @returns {string|null} The "Learn More" link HTML or null if the link should not be displayed.
 */
const getLearnMoreLink = (
  vertical,
  isNoneDisclaimer,
  hideLearnMore,
  learnMoreLinkOverride,
) => {
  const shouldAddLearnMoreLink = !!learnMoreLinkOverride || (vertical === 'today' && !isNoneDisclaimer && !hideLearnMore);

  if (!shouldAddLearnMoreLink) {
    return null;
  }

  const learnMoreLink = learnMoreLinkOverride || ECOMMERCE_ABOUT_URL_TODAY;

  return `<a data-testid="disclaimer-learn-more" href="${learnMoreLink}">Learn more.</a>`;
};

/**
 * DisclaimerModal component displays a modal with disclaimer information.
 *
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children - The content to be displayed inside the modal.
 * @param {'DEFAULT' | 'PRIME_DAY' | 'SELECT_DEFAULT' | 'NONE' | 'OVERRIDE'} [props.disclaimerKey='DEFAULT'] - The key for the disclaimer text.
 * @param {string} [props.disclaimerOverride] - Override text for the disclaimer.
 * @param {boolean} [props.showCloseButton=false] - Flag to show or hide the close button.
 * @param {boolean} [props.small=false] - Flag to display a smaller version of the modal.
 * @param {boolean} [props.hideLearnMore=false] - Flag to hide the "Learn More" link.
 * @param {string} [props.learnMoreLinkOverride] - Override URL for the "Learn More" link.
 */
function DisclaimerModal({
  children,
  disclaimerKey: key = 'DEFAULT',
  disclaimerOverride: override,
  showCloseButton = false,
  small = false,
  hideLearnMore = false,
  learnMoreLinkOverride,
}) {
  const [open, setOpen] = React.useState(false);
  const vertical = React.useContext(VerticalContext);

  const handleClick = () => {
    setOpen(!open);
  };

  const closeModal = () => {
    setOpen(false);
  };

  const getDisclaimerText = () => {
    const hasOverride = key === 'OVERRIDE'
      && typeof override?.disclaimerOverride === 'string'
      && override?.disclaimerOverride.length > 0;
    const disclaimerText = ECOMMERCE_DISCLAIMER[key] || ECOMMERCE_DISCLAIMER.DEFAULT;
    const isNoneDisclaimer = key === 'NONE';

    // If override is provided then use the override text,
    // otherwise use the vertical specific text and
    // if not then use the default text
    let text = hasOverride ? override.disclaimerOverride : disclaimerText;
    const learnMoreLink = getLearnMoreLink(
      vertical,
      isNoneDisclaimer,
      hideLearnMore,
      learnMoreLinkOverride,
    );

    if (learnMoreLink) {
      text += ` ${learnMoreLink}`;
    }

    return text;
  };

  return (
    <div
      className={classNames(
        'disclaimer-modal',
        styles.disclaimerModal,
        { [styles.open]: open },
      )}
      data-test="disclaimer"
      data-testid="disclaimer"
    >
      {children ? (
        // Toggle text provided by parent component
        <>
          {children}
          <button
            className={styles.infoBtn}
            data-test="toggle-btn"
            onClick={handleClick}
            type="button"
          >
            <span className="icon icon-info" />
          </button>
        </>
      ) : (
        // Default toggle option
        <>
          <button
            className={styles.toggleBtn}
            data-test="toggle-btn"
            onClick={handleClick}
            type="button"
          >
            {open ? 'Hide Disclaimer' : 'Show Disclaimer'}
            <IconCaret />
          </button>
          <div className={styles.line} />
        </>
      )}

      {/* Card containing disclaimer text */}
      {open && (
        <div
          className={classNames(
            styles.card,
            { [styles.small]: small },
          )}
          data-test="disclaimer-box"
          data-testid="disclaimer-box"
        >
          {showCloseButton && (
            <button
              className="css-reset-btn absolute"
              onClick={closeModal}
              type="button"
            >
              <span className="icon icon-close f2" />
            </button>
          )}
          <p className={styles.text} data-testid="disclaimer-text">
            <div dangerouslySetInnerHTML={{ __html: getDisclaimerText() }} />
          </p>
        </div>
      )}
    </div>
  );
}

export { DisclaimerModal };
